import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";

import { ConfigurationService } from "@core/configuration.service";
import { DocumentController } from "./document-controller";
import { ProcessDocumentType, WorkflowRouteDto, WorkflowRouteRequestDto } from "@helper/abstraction/workflows";
import { DocumentSignedDto } from "@helper/abstraction/documents";
import { DocumentForSignDto } from "@helper/abstraction/roseu";
import { Status } from "@helper/abstraction/status";
import * as AbstractionWorkflow from "@helper/abstraction/workflow-template";
import { WorkflowStageTemplateDto } from "@helper/abstraction/workflow-template";
import { FilterRequestParams } from "@helper/abstraction/filter";

export class Workflows extends DocumentController<any> {
	public get apiUrl(): string {
		const api = this.config.api.workflows;
		return `${api}`;
	}

	public types = {
		statuses$: (): Observable<Status[]> => {
			const url = `${this.config.api.root}/workflows/types/statuses/list`;

			return this.http.get<Status[]>(url, { withCredentials: true });
		},
		userDecisionStatuses$: (): Observable<Status[]> => {
			const url = `${this.config.api.root}/workflows/types/userDecisionStatuses/list`;

			return this.http.get<Status[]>(url, { withCredentials: true });
		},
		documentTypes$: (): Observable<Status[]> => {
			const url = `${this.config.api.root}/workflows/types/documentTypes/list`;

			return this.http.get<Status[]>(url, { withCredentials: true });
		},
		userDecisionStatusesState$: (userDecisionState: string): Observable<Status[]> => {
			const url = `${this.config.api.root}/workflows/types/userDecisionStatuses/${userDecisionState}/list`;

			return this.http.get<Status[]>(url, { withCredentials: true });
		},
	};

	public templates = {
		list$: (queryParams: { processingStatus: string; name?: string }): Observable<Status[]> => {
			const url = `${this.config.api.root}/workflows/template/list`;
			let params = new HttpParams();
			params = params.set("processingStatus", queryParams.processingStatus.toString());
			if (queryParams.name) {
				params = params.set("name", queryParams.name.toString());
			}

			return this.http.get<Status[]>(url, { params, withCredentials: true });
		},
	}

	public route = {
		start$: (data: WorkflowRouteRequestDto): Observable<number> => {
			const url = `${this.config.api.workflowsRoute}/start`;

			return this.http.post<number>(url, data, { withCredentials: true });
		},
		get$: (currentRouteId: number): Observable<WorkflowRouteDto> => {
			const url = `${this.config.api.workflowsRoute}/${currentRouteId}`;

			return this.http.get<WorkflowRouteDto>(url, { withCredentials: true });
		},
		cancel$: (currentRouteId: number): Observable<void> => {
			const url = `${this.config.api.workflowsRoute}/${currentRouteId}/cancel`;

			return this.http.post<void>(url, { withCredentials: true });
		},
		upcomingStages$: (currentRouteId: number): Observable<WorkflowStageTemplateDto[]> => {
			const url = `${this.config.api.workflowsRoute}/${currentRouteId}/upcomingStages`;

			return this.http.get<WorkflowStageTemplateDto[]>(url, { withCredentials: true });
		},
	}

	public userDecisions​ = {
		processDocument$: (processType: ProcessDocumentType, userDecisionId: number, message?: string): Observable<void> => {
			const url = `${this.config.api.workflows}/${userDecisionId}/${processType}`;
			let params = new HttpParams();
			if (message) {
				params = params.set("message", message.toString());
			}

			return this.http.post<void>(url, {}, { params, withCredentials: true });
		},
		prepare$: (userDecisionId: number, base64 = true): Observable<DocumentForSignDto> => {
			const url = `${this.config.api.workflows}/${userDecisionId}/sign/prepare`;
			let params = new HttpParams();
				params = params.set("base64", base64);

			return this.http.get<DocumentForSignDto>(url, { params, withCredentials: true });
		},
		finalize$: (userDecisionId: number, signedData: DocumentSignedDto, message: string): Observable<void> => {
			const url = `${this.config.api.workflows}/${userDecisionId}/sign/finalize`;
			let params = new HttpParams();
			if (message) {
				params = params.set("message", message);
			}

			return this.http.post<void>(url, signedData, { params, withCredentials: true });
		},
	}

	public workflowTemplates = {
		list: {
			post$: (filter: AbstractionWorkflow.WorkflowTemplatePageFilterDto, queryParams: FilterRequestParams): Observable<AbstractionWorkflow.WorkflowDto> => {
				const url = `${this.config.api.root}/workflows/template/filteredPage`;
				let params = new HttpParams();
				params = params.set("page", queryParams.page.toString());
				params = params.set("size", queryParams.size.toString());
				return this.http.post<AbstractionWorkflow.WorkflowDto>(url, filter, { params, withCredentials: true });
			}
		},
		documentTypesList: {
			get$: (): Observable<AbstractionWorkflow.CommonInfoDto[]> => {
				const url = `${this.config.api.root}/workflows/types/documentTypes/list`;
				return this.http.get<AbstractionWorkflow.CommonInfoDto[]>(url, { withCredentials: true });
			}
		},
		template: {
			get$: (id: number): Observable<AbstractionWorkflow.WorkflowTemplateDto> => {
				const url = `${this.config.api.root}/workflows/template/${id}`;
				return this.http.get<AbstractionWorkflow.WorkflowTemplateDto>(url, { withCredentials: true });
			},
			post$: (workflow: AbstractionWorkflow.WorkflowTemplateRequestDto): Observable<AbstractionWorkflow.WorkflowTemplateDto> => {
				const url = `${this.config.api.root}/workflows/template`;
				return this.http.post<AbstractionWorkflow.WorkflowTemplateDto>(url, workflow, { withCredentials: true });
			},
			put$: (id: number, workflowTemplate: AbstractionWorkflow.WorkflowTemplateRequestDto): Observable<AbstractionWorkflow.WorkflowTemplateDto> => {
				const url = `${this.config.api.root}/workflows/template/${id}`;
				return this.http.put<AbstractionWorkflow.WorkflowTemplateDto>(url, workflowTemplate, { withCredentials: true });
			},
			delete$: (id: number): Observable<void> => {
				const url = `${this.config.api.root}/workflows/template/${id}`;
				return this.http.delete<void>(url, { body: id, withCredentials: true });
			},
		},
	}

	constructor(
		public config: ConfigurationService,
		public http: HttpClient
	) {
		super(http);
	}
}
